import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useMatch, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import { getUI, getUITopBarControls, getUIMetadata } from './uiRegistry';
import { getSlcTaskSectionLabel } from './uis/SlideLevelClassificationUI/slideLevelClassificationModel';
import { normalizeCompletionPolicy } from './completionPolicy';
import {
    getRoiConfig,
    hasRoiTypesConfigured,
} from './uis/BoundingBoxUI/roiProjectConfig';
import NotesControl from '../../components/NotesControl';
import { ItemMetadataCapsule } from './itemMetadata/ItemMetadataCapsule';
import { findItemRowForItemName } from './itemMetadata/itemInfoUtils';
import {
    normalizeItemMetadataConfig,
    getDisplayableItemMetadataEntries,
} from './itemMetadata/itemMetadataModel';
import ViewerTopBar from './ViewerTopBar';
import ProjectRoiNavigator from './uis/ProjectRoiNavigator';
import styles from './ProjectViewer.module.css';

/**
 * ROI edit route (`/roi-edit/`) mounts the bounding-box viewer module (rectangle/select +
 * BoundingBox TopBarControls) whenever the user is eligible for ROI editing, regardless of
 * the project's configured annotationUI.id (e.g. ruler-measurement). Project `uiConfig` is
 * unchanged — ROI types/enabled still come from the same config object.
 */

/** ViewerTopBar copy on `/projects/:folderId/roi-edit/:itemId` (context lives in ⓘ instructions). */
const ROI_EDIT_TASK_SECTION_LABEL = 'Regions of interest:';
const ROI_EDIT_INSTRUCTIONS =
    'You are editing the shared ROI layer for this project. Use the ROI type pills and Draw mode to place rectangles; they save to the project ROI document (read-only guidance for participants), not to your personal annotation.';

function userIsProjectAdmin(projectDoc, user) {
    if (!projectDoc || !user) return false;
    if (user.admin) return true;
    const parts = projectDoc.girderProjects?.participants ?? [];
    return parts.some(
        (p) => String(p.userId) === String(user._id) && (p.roles ?? []).includes('admin')
    );
}

function ProjectViewer() {
    const { folderId, itemId } = useParams();
    const navigate = useNavigate();
    const onRoiEditPath = Boolean(
        useMatch({ path: '/projects/:folderId/roi-edit/:itemId', end: true }),
    );
    const { user: authUser } = useAuth();

    const [projectDoc, setProjectDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [progressMap, setProgressMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Save status, completable, and resettable are reported by TopBarControls via callbacks.
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveError, setSaveError] = useState(null);
    const [completable, setCompletable] = useState(false);
    /** Strict completeness from the active UI's TopBarControls (for confirm-before-finish). */
    const [strictComplete, setStrictComplete] = useState(false);
    const [incompleteReasons, setIncompleteReasons] = useState([]);
    const [resettable, setResettable] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);
    const [clearSelectedEnabled, setClearSelectedEnabled] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveMode, setSaveMode] = useState(null); // 'manual' | 'auto' | null
    const [manualSaveAction, setManualSaveAction] = useState(null);
    const [validationMessage, setValidationMessage] = useState(null);
    // completed is a local toggle for this session (stub — future stages will persist it).
    const [completed, setCompleted] = useState(false);

    // toolkitReady gates TopBarControls mount. It is set true when the UI calls
    // onToolkitReady, and reset to false whenever the current item changes so that
    // TopBarControls remounts cleanly for each new item/toolkit instance.
    const [toolkitReady, setToolkitReady] = useState(false);
    /** Bumped when annotation hooks merge features into the toolkit (ROI overlay etc.) for shared ROI navigator. */
    const [roiLayerVersion, setRoiLayerVersion] = useState(0);

    // Shared refs passed down to both the UI component and TopBarControls so they
    // can communicate without lifting all measurement state to this level.
    const toolkitRef = useRef(null);
    const activateSlotRef = useRef(null);
    const resetAllRef = useRef(null);
    const revertToSavedRef = useRef(null);
    const deleteSelectedRef = useRef(null);
    const exportDataRef = useRef(null);

    const fetchProgress = useCallback(() => {
        if (!folderId) return;
        api.get(`projects/${folderId}/progress`, {})
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data?.progress)) {
                    const map = Object.fromEntries(
                        res.data.progress.map((p) => [
                            p.itemId,
                            p.status || 'not_started',
                        ]),
                    );
                    setProgressMap(map);
                }
            })
            .catch(() => {});
    }, [folderId]);

    useEffect(() => {
        setLoading(true);
        setError(null);

        Promise.all([
            api.get(`projects/${folderId}`, {}),
            api.get(`projects/${folderId}/items`, {}),
        ])
            .then(([projectRes, itemsRes]) => {
                if (projectRes.status !== 200) {
                    setError(projectRes.data?.message || 'Failed to load project.');
                    return;
                }
                setProjectDoc(projectRes.data);

                if (itemsRes.status === 200) {
                    const itemList = itemsRes.data?.items ?? [];
                    setItems(itemList);
                    if (itemList.length > 0) {
                        fetchProgress();
                    } else {
                        setProgressMap({});
                    }
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [folderId, fetchProgress]);

    const handleCurrentItemChange = useCallback(
        (newItem) => {
            const suffix = onRoiEditPath ? 'roi-edit' : 'view';
            navigate(`/projects/${folderId}/${suffix}/${newItem._id}`);
        },
        [folderId, navigate, onRoiEditPath],
    );

    const handleComplete = useCallback((value) => {
        setCompleted(value);
    }, []);

    // Called when an annotation document is loaded for the current item. This
    // allows us to initialise the completed state from the server-side flag.
    const handleAnnotationLoaded = useCallback((annotationDoc) => {
        const status = annotationDoc?.girderProjects?.status;
        if (status === 'completed') {
            setCompleted(true);
        } else {
            setCompleted(false);
        }
    }, []);

    const handleSaveStatusChange = useCallback((status) => {
        setSaveStatus(status);
        if (status === 'error') {
            setSaveError(saveMode === 'manual'
                ? 'Save failed. Your changes are still unsaved.'
                : 'Auto-save failed. Your changes may not be saved.');
        } else {
            setSaveError(null);
        }
    }, [saveMode]);

    const handleDirtyStateChange = useCallback((isDirty) => {
        setHasUnsavedChanges(Boolean(isDirty));
    }, []);

    const handleSaveModeChange = useCallback((mode) => {
        setSaveMode(mode || null);
    }, []);

    const handleManualSaveActionChange = useCallback((action) => {
        setManualSaveAction(() => (typeof action === 'function' ? action : null));
    }, []);

    const handleAnnotationCompletionStatusChange = useCallback((status) => {
        if (!status || typeof status !== 'object') return;
        setCompletable(Boolean(status.completable));
        setStrictComplete(Boolean(status.strictComplete));
        setIncompleteReasons(Array.isArray(status.incompleteReasons) ? status.incompleteReasons : []);
    }, []);

    const handleResettableChange = useCallback((isResettable) => {
        setResettable(isResettable);
    }, []);

    const handleValidationMessage = useCallback((message) => {
        setValidationMessage(message ?? null);
    }, []);

    const handleSelectionChange = useCallback((selection) => {
        setHasSelection(selection);
    }, []);

    const handleClearSelectedEnabledChange = useCallback((enabled) => {
        setClearSelectedEnabled(Boolean(enabled));
    }, []);

    // Called by the UI component once AnnotationToolkit is fully initialised.
    // Flipping toolkitReady to true causes TopBarControls to mount, at which point
    // toolkitRef.current is already populated — no polling needed.
    const handleToolkitReady = useCallback(() => {
        setToolkitReady(true);
    }, []);

    const handleToolkitLayersUpdated = useCallback(() => {
        setRoiLayerVersion((v) => v + 1);
    }, []);

    // When the item changes, clear the toolkit ref and readiness before paint so
    // TopBarControls never queries a disposed viewer while toolkitReady is still true.
    useLayoutEffect(() => {
        setToolkitReady(false);
        toolkitRef.current = null;
        setCompletable(false);
        setStrictComplete(false);
        setIncompleteReasons([]);
        setResettable(false);
        setHasSelection(false);
        setClearSelectedEnabled(false);
        setHasUnsavedChanges(false);
        setSaveMode(null);
        setManualSaveAction(null);
        setCompleted(false);
        setSaveStatus('idle');
        setSaveError(null);
        setValidationMessage(null);
    }, [itemId]);

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [hasUnsavedChanges]);

    const blocker = useBlocker(hasUnsavedChanges);

    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        const ok = window.confirm('You have unsaved annotation changes. Leave this page and discard them?');
        if (ok) blocker.proceed();
        else blocker.reset();
    }, [blocker]);

    const uiConfigForMetadata = projectDoc?.girderProjects?.annotationUI?.config ?? {};
    const itemMetadataNormalized = useMemo(
        () => normalizeItemMetadataConfig(uiConfigForMetadata),
        [uiConfigForMetadata],
    );
    const itemMetadataRows = itemMetadataNormalized.itemMetadata?.rows ?? [];
    const itemMetadataMatchOpts = useMemo(() => ({
        trimWhitespace: itemMetadataNormalized.itemMetadata?.trimWhitespace !== false,
        caseInsensitive: Boolean(itemMetadataNormalized.itemMetadata?.caseInsensitive),
    }), [itemMetadataNormalized]);

    // ── Error / loading states ─────────────────────────────────────────────
    if (loading) {
        return <div className={styles.loading}>Loading project…</div>;
    }

    if (error) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>{error}</div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    const girderProjects = projectDoc?.girderProjects ?? {};
    const uiId = girderProjects.annotationUI?.id;
    const uiConfig = girderProjects.annotationUI?.config ?? {};
    const notesConfig = girderProjects.notes ?? null;
    const completionPolicy = normalizeCompletionPolicy(uiConfig);
    const roiEditMode =
        onRoiEditPath
        && userIsProjectAdmin(projectDoc, authUser)
        && hasRoiTypesConfigured(uiConfig)
        && getRoiConfig(uiConfig).enabled;
    const runtimeUiId = roiEditMode ? 'bounding-box' : uiId;
    const runtimeMetadata = getUIMetadata(runtimeUiId);
    const resetMenuClearSelected = Boolean(runtimeMetadata?.clearSelectedInResetMenu);

    if (!uiId) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>No annotation UI configured for this project.</div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    const UIComponent = getUI(runtimeUiId);
    if (!UIComponent) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>
                    {roiEditMode ? (
                        <>ROI editor is unavailable (bounding-box UI module missing).</>
                    ) : (
                        <>Unknown annotation UI: <strong>{uiId}</strong>.</>
                    )}
                </div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>This project has no items.</div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    const currentItem = items.find((it) => String(it._id) === itemId) ?? items[0];
    const currentItemId = String(currentItem._id);

    const metadataRow = currentItem?.name
        ? findItemRowForItemName(
            itemMetadataRows,
            currentItem.name,
            itemMetadataMatchOpts,
        )
        : null;
    const itemMetadataEntries = getDisplayableItemMetadataEntries(uiConfig, metadataRow);
    const itemMetadataBar = itemMetadataEntries.length > 0 ? (
        <ItemMetadataCapsule
            uiConfig={uiConfig}
            row={metadataRow}
        />
    ) : null;
    const itemMetadataPlacement = runtimeMetadata?.itemMetadataPlacement ?? 'right';

    // TopBarControls is an optional named export from the UI module.
    // It is always rendered so the topbar chrome stays visible during item
    // navigation. toolkitReady is passed as a prop so TopBarControls can
    // disable interactive elements until the toolkit is initialised.
    const TopBarControls = getUITopBarControls(runtimeUiId);

    const taskControls = TopBarControls ? (
        <Suspense fallback={null}>
            <TopBarControls
                uiConfig={uiConfig}
                folderId={folderId}
                itemId={currentItemId}
                toolkitRef={toolkitRef}
                activateSlotRef={activateSlotRef}
                resetAllRef={resetAllRef}
                revertToSavedRef={revertToSavedRef}
                deleteSelectedRef={deleteSelectedRef}
                completed={completed}
                toolkitReady={toolkitReady}
                onSaveStatusChange={handleSaveStatusChange}
                onAnnotationCompletionStatusChange={handleAnnotationCompletionStatusChange}
                onResettableChange={handleResettableChange}
                onSelectionChange={handleSelectionChange}
                onClearSelectedEnabledChange={handleClearSelectedEnabledChange}
                onAnnotationLoaded={handleAnnotationLoaded}
                onProgressUpdated={fetchProgress}
                onDirtyStateChange={handleDirtyStateChange}
                onSaveModeChange={handleSaveModeChange}
                onManualSaveAction={handleManualSaveActionChange}
                onValidationMessage={handleValidationMessage}
                exportDataRef={exportDataRef}
                roiEditMode={roiEditMode}
                onToolkitLayersUpdated={handleToolkitLayersUpdated}
            />
        </Suspense>
    ) : null;

    return (
        <div className={styles.viewerPage}>
            <ViewerTopBar
                itemNavAccessory={
                    <ProjectRoiNavigator
                        toolkitRef={toolkitRef}
                        toolkitReady={toolkitReady}
                        roiConfig={getRoiConfig(uiConfig)}
                        roiEditMode={roiEditMode}
                        itemId={currentItemId}
                        disabled={!toolkitReady || (!roiEditMode && completed)}
                        roiLayerVersion={roiLayerVersion}
                    />
                }
                itemMetadata={itemMetadataBar}
                itemMetadataPlacement={itemMetadataPlacement}
                taskAccessory={
                    (!roiEditMode && notesConfig?.enabled) ? (
                        <NotesControl
                            folderId={folderId}
                            itemId={currentItemId}
                            config={notesConfig}
                            readOnly={completed}
                        />
                    ) : null
                }
                folderId={folderId}
                projectName={projectDoc?.name}
                items={items}
                currentItem={currentItem}
                onItemChange={handleCurrentItemChange}
                roiEditMode={roiEditMode}
                taskControls={taskControls}
                instructions={
                    roiEditMode
                        ? ROI_EDIT_INSTRUCTIONS
                        : (uiConfig?.instructions || null)
                }
                saveStatus={saveStatus}
                saveError={saveError}
                saveMode={saveMode}
                hasUnsavedChanges={hasUnsavedChanges}
                onManualSave={manualSaveAction}
                completable={roiEditMode ? false : completable}
                completed={roiEditMode ? false : completed}
                strictComplete={strictComplete}
                incompleteReasons={incompleteReasons}
                completionPolicy={completionPolicy}
                taskSectionLabel={
                    roiEditMode
                        ? ROI_EDIT_TASK_SECTION_LABEL
                        : (uiId === 'slide-level-classification'
                            ? getSlcTaskSectionLabel(uiConfig, runtimeMetadata?.taskSectionLabel)
                            : (runtimeMetadata?.taskSectionLabel ?? 'Annotations:'))
                }
                taskSectionLabelMode={
                    roiEditMode
                        ? 'normal'
                        : (runtimeMetadata?.taskSectionLabelMode ?? 'normal')
                }
                onComplete={handleComplete}
                resettable={resettable}
                onReset={() => {
                    setCompleted(false);
                    resetAllRef.current?.();
                }}
                onRevertToSaved={() => revertToSavedRef.current?.()}
                hasSelection={hasSelection}
                onDeleteSelected={() => deleteSelectedRef.current?.()}
                resetMenuClearSelected={resetMenuClearSelected}
                clearSelectedEnabled={clearSelectedEnabled}
                showViewerDeleteButton={!runtimeMetadata?.deleteInTaskControls}
                validationMessage={validationMessage}
                progressMap={progressMap}
                onFinishedPersisted={fetchProgress}
                getExportData={() => exportDataRef.current?.()}
            />
            <div className={styles.viewerBody}>
                <Suspense fallback={<div className={styles.loading}>Loading UI…</div>}>
                    <UIComponent
                        projectConfig={girderProjects}
                        uiConfig={uiConfig}
                        items={items}
                        currentItem={currentItem}
                        folderId={folderId}
                        onCurrentItemChange={handleCurrentItemChange}
                        onComplete={handleComplete}
                        onSaveStatusChange={handleSaveStatusChange}
                        toolkitRef={toolkitRef}
                        activateSlotRef={activateSlotRef}
                        onToolkitReady={handleToolkitReady}
                    />
                </Suspense>
            </div>
        </div>
    );
}

export default ProjectViewer;
